import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useQuotationWorkspace, useSection3Health } from './section3Api';
import { Section3StateView } from './Section3StateView';
import { formatMinor } from './section5Format';

/** 24.3 — quotation workspace extending Sales Documents. Totals are server-
 *  authoritative; revision history is immutable; approval/delivery are gated. */
export function QuotationWorkspacePanel({ salesDocId = null }: { salesDocId?: string | null }) {
  const health = useSection3Health();
  const q = useQuotationWorkspace(salesDocId);
  const d = q.data;

  return (
    <Card>
      <CardHeader
        title="Quotation workspace (Section 3)"
        subtitle="Server-authoritative totals, immutable revisions, approval and delivery gates."
        action={health.data ? <Badge tone="info">Scope: {health.data.scope}</Badge> : null}
      />
      <CardBody>
        {!salesDocId ? (
          <EmptyState title="No document selected" description="Open a quotation sales document to view its revisions, totals and approval state." />
        ) : (
          <Section3StateView
            isLoading={q.isLoading}
            isError={q.isError}
            error={q.error}
            isFetching={q.isFetching}
            isEmpty={!!d && d.revisions.length === 0}
            emptyTitle="No revisions yet"
            emptyDescription="Quotation revisions will appear here once created."
            prereqTitle="Quotation workspace unavailable"
            prereqDescription="No quotation workspace is available for this document in your current scope. The record may not exist, may be outside your access, or may not belong to the governed quotation workflow."
          >
            {d && (
              <div className="space-y-3">
                {d.approvalRequired && <Badge tone="warning">Approval required</Badge>}
                {d.revisions.map((r) => (
                  <div key={r.id} className="rounded-card border border-outline p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-data-md font-semibold text-on-surface">Revision {r.revisionNumber}</span>
                      <span className="text-data-md text-on-surface">{formatMinor(r.currency, r.totalMinor)}</span>
                    </div>
                    <p className="mt-1 text-body-sm text-on-surface-variant">Approval: {r.approvalState}</p>
                  </div>
                ))}
              </div>
            )}
          </Section3StateView>
        )}
      </CardBody>
    </Card>
  );
}
